import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Waitlist = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, { email, phone: phone || null, language: i18n.language });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      toast.error(t("waitlist.error"));
    }
  };

  return (
    <section id="waitlist" data-testid="waitlist-section" className="py-24 lg:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="relative overflow-hidden rounded-3xl border border-mint/25 bg-gradient-to-br from-mint/10 via-card to-night p-10 lg:p-20"
        >
          <div className="absolute -left-24 -bottom-24 w-80 h-80 rounded-full bg-mint/15 blur-[110px] pointer-events-none" />
          <div className="relative max-w-2xl mx-auto text-center">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-mint mb-4">{t("waitlist.kicker")}</p>
            <h2 className="font-display font-semibold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl text-balance">
              {t("waitlist.title")}
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/60">{t("waitlist.sub")}</p>

            {status === "success" ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: EASE }}
                data-testid="waitlist-success-message"
                className="mt-10 rounded-2xl border border-mint/30 bg-mint/10 p-8"
              >
                <CheckCircle2 className="w-10 h-10 text-mint mx-auto" strokeWidth={1.5} />
                <h3 className="mt-4 font-display font-semibold text-white text-xl">{t("waitlist.successTitle")}</h3>
                <p className="mt-2 text-sm text-white/60">{t("waitlist.successText")}</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="waitlist-form" className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("waitlist.emailPlaceholder")}
                  data-testid="waitlist-email-input"
                  className="flex-1 rounded-full border border-white/15 bg-night/70 px-6 py-3.5 text-sm text-white placeholder:text-white/35 outline-none focus:border-mint/60 transition-colors duration-200"
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t("waitlist.phonePlaceholder")}
                  data-testid="waitlist-phone-input"
                  className="sm:w-48 rounded-full border border-white/15 bg-night/70 px-6 py-3.5 text-sm text-white placeholder:text-white/35 outline-none focus:border-mint/60 transition-colors duration-200"
                />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="waitlist-submit-button"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-mint px-7 py-3.5 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                      {t("waitlist.sending")}
                    </>
                  ) : (
                    <>
                      {t("waitlist.cta")}
                      <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={2} />
                    </>
                  )}
                </button>
              </form>
            )}

            <p data-testid="waitlist-privacy-note" className="mt-6 text-xs text-white/35">{t("waitlist.privacy")}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
